export type MobilePlatform = 'ios' | 'android' | 'other';

const hasNavigator = typeof navigator !== 'undefined';

/**
 * Best-effort detection of the mobile operating system, used by the public /mobile landing page
 * to route a driver to the correct app store.
 *
 * - Android is matched first as a simple, unambiguous signal.
 * - iPhone / iPod / older iPads are matched by the classic tokens.
 * - Modern iPadOS Safari reports a desktop "Macintosh" user agent, so a Macintosh UA that also
 *   exposes touch points (which real Macs do not) is treated as iOS — otherwise iPads would fall
 *   through to "other" and get the desktop layout.
 *
 * @param userAgent the navigator user-agent string (injectable for testing)
 * @param maxTouchPoints navigator.maxTouchPoints (injectable for testing)
 * @returns 'ios', 'android', or 'other' (desktop / unknown)
 */
export const getMobilePlatform = (
  userAgent: string = hasNavigator ? navigator.userAgent : '',
  maxTouchPoints: number = hasNavigator ? navigator.maxTouchPoints : 0,
): MobilePlatform => {
  if (/android/i.test(userAgent)) {
    return 'android';
  }

  const isClassicIos = /iphone|ipad|ipod/i.test(userAgent);
  const isIpadOsAsMac = /macintosh/i.test(userAgent) && maxTouchPoints > 1;
  if (isClassicIos || isIpadOsAsMac) {
    return 'ios';
  }

  return 'other';
};
